import { useState, type ReactNode } from 'react';
import { DeniedState, EmptyState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { styles } from '@/shared/app-detail-layout/styles';
import { DetailHeader } from '@/shared/app-detail-layout/header';
import { DetailBody } from '@/shared/app-detail-layout/sections';
import { MobileStickyBar } from '@/shared/app-detail-layout/mobile-bar';
import { Breadcrumbs } from '@/shared/app-detail-layout/breadcrumbs';
import type { AppDetailLayoutProps, DetailBreadcrumb } from '@/shared/app-detail-layout/types';

/**
 * Decides which single surface renders.
 *
 * Precedence is fixed and deliberate: denial outranks not-found, which outranks
 * a generic error, which outranks loading. A denied user must not learn whether
 * a record exists, so `denied` is checked FIRST — rendering "not found" for a
 * record they were not allowed to see would leak its existence.
 */
function resolveSurface(props: AppDetailLayoutProps): 'denied' | 'notFound' | 'error' | 'loading' | 'ready' {
  if (props.isDenied) return 'denied';
  if (props.notFound) return 'notFound';
  if (props.error) return 'error';
  if (props.isLoading) return 'loading';
  return 'ready';
}

/**
 * Non-ready surfaces render in place of the body, leaving the header visible so
 * the title still orients the user.
 */
function NonReadySurface({ surface, error }: { surface: string; error?: Error | null }): ReactNode {
  if (surface === 'denied') {
    return <DeniedState />;
  }
  if (surface === 'notFound') {
    return <EmptyState title="Not found" description="This record does not exist, or is no longer available." />;
  }
  if (surface === 'error') {
    return <ErrorState title="Something went wrong" message={error?.message ?? 'Please try again.'} />;
  }
  return <LoadingSkeleton />;
}

/**
 * The orchestrator. Owns page-level concerns: the surface picker, the active
 * tab (shared between the header tab strip and the body panels when
 * `tabsInHeader`), and the mobile sticky bar.
 *
 * Module-agnostic: it never imports a module. Consumers pass typed
 * descriptors; they do not pass markup for the frame.
 */
export function AppDetailLayout(props: AppDetailLayoutProps): ReactNode {
  const surface = resolveSurface(props);

  // Active tab is LAYOUT state. When `tabsInHeader` is true, the same id drives
  // both the header tab strip and the body panel. Otherwise the body tab
  // strip is the source of truth and we ignore the lifted state — the body
  // keeps its own internal hook.
  const firstTabId = props.tabs && props.tabs.length > 0 ? props.tabs[0]?.id ?? '' : '';
  const [activeTabId, setActiveTabId] = useState(firstTabId);

  // Derive the effective id instead of resetting state in an effect. If the
  // consumer navigates to a new record with a different tabs array, the stored
  // id will no longer match any tab — fall back to the first tab so the
  // header strip stays in sync without a setState-in-effect.
  const tabs = props.tabs ?? [];
  const storedIsValid = tabs.some((tab) => tab.id === activeTabId);
  const effectiveActiveTabId = storedIsValid ? activeTabId : firstTabId;

  return (
    <section
      className={styles.page}
      aria-label={props.title}
      aria-busy={props.isLoading ? true : undefined}
    >
      <BreadcrumbsAbove
        accent={props.headerVariant === 'accent'}
        breadcrumbs={props.breadcrumbs}
        primaryAction={props.primaryAction}
        secondaryAction={props.secondaryAction}
      />

      <DetailHeader
        title={props.title}
        subtitle={props.subtitle}
        status={props.status}
        backTo={props.backTo}
        badges={props.badges}
        prev={props.prev}
        next={props.next}
        primaryAction={props.primaryAction}
        secondaryAction={props.secondaryAction}
        actions={props.actions}
        tabs={props.tabs}
        activeTabId={effectiveActiveTabId}
        onTabChange={setActiveTabId}
        tabsInHeader={props.tabsInHeader}
        headerVariant={props.headerVariant}
      />

      {props.toolbar ? <div className={styles.toolbar}>{props.toolbar}</div> : null}

      {surface === 'ready' ? (
        <div className={styles.body}>
          <div className={styles.main}>
            <DetailBody
              sections={props.sections}
              tabs={props.tabs}
              tabsInHeader={props.tabsInHeader}
              activeTabId={effectiveActiveTabId}
            />
          </div>
          {props.aside ? <div className={styles.aside}>{props.aside}</div> : null}
        </div>
      ) : (
        <div className={styles.body}>
          <NonReadySurface surface={surface} error={props.error} />
        </div>
      )}

      <MobileStickyBar actions={props.mobileActions} />
    </section>
  );
}

/**
 * The strip ABOVE the accent card. Three slots:
 * - breadcrumbs on the left (when supplied)
 * - primary + secondary actions on the right
 *
 * The whole strip reads as "page navigation + actions", not as a card
 * header — the actions live alongside the breadcrumb, not inside the card.
 *
 * The neutral header variant does not use this strip — its `backTo` link
 * handles the breadcrumb case and its `actions` slot sits inside the header.
 */
function BreadcrumbsAbove({
  accent,
  breadcrumbs,
  primaryAction,
  secondaryAction,
}: {
  accent: boolean;
  breadcrumbs?: ReadonlyArray<DetailBreadcrumb>;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
}): ReactNode {
  if (!accent) return null;
  const hasBreadcrumbs = !!(breadcrumbs && breadcrumbs.length > 0);
  const hasActions = !!(primaryAction || secondaryAction);
  if (!hasBreadcrumbs && !hasActions) return null;
  return (
    <div className={styles.breadcrumbsRow}>
      {hasBreadcrumbs ? <Breadcrumbs breadcrumbs={breadcrumbs ?? []} /> : null}
      {hasActions ? (
        <div className={styles.pageActions}>
          {primaryAction}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}